import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import type { AppRole } from '@/lib/supabase'
import { FullPageMessage } from '@/components/FullPageMessage'

export function RequireAuth() {
  const { session, profile, loading } = useAuth()
  const location = useLocation()

  if (loading) return <FullPageMessage title="Loading" />
  if (!session) return <Navigate to="/sign-in" state={{ from: location.pathname }} replace />

  // Signed in, but not yet attached to an organisation or the admin allowlist.
  // handle_new_user leaves these accounts inactive on purpose.
  if (profile && !profile.is_active) return <Navigate to="/waiting" replace />

  return <Outlet />
}

export function RequireRole({ roles }: { roles: AppRole[] }) {
  const { profile, loading } = useAuth()

  if (loading) return <FullPageMessage title="Loading" />
  if (!profile || !roles.includes(profile.role)) {
    return (
      <FullPageMessage
        title="Not available on this account"
        body="Your account does not have access to this part of the portal. If that looks wrong, contact the Ki Tua o Matariki team."
      />
    )
  }
  return <Outlet />
}

/** R7: a lapsed subscription keeps the account but closes the gated content. */
export function RequirePortalAccess() {
  const { hasPortalAccess, profile, loading } = useAuth()

  if (loading) return <FullPageMessage title="Loading" />
  if (!hasPortalAccess) {
    const org = profile?.organisations
    return (
      <FullPageMessage
        title="Subscription not active"
        body={
          org
            ? `${org.name} does not currently hold portal access. Talk to the Ki Tua o Matariki team about renewing, and your resources and data will come straight back.`
            : 'This account is not linked to a partner organisation yet.'
        }
      />
    )
  }
  return <Outlet />
}
